import { Fragment, useMemo } from "react";
import { useMediaSelector } from "media-chrome/react/media-store";
import { cn } from "@/lib/utils";
import { MentionCard, MentionV4 } from "@/types/MentionCard";

export default function Karaoke({
  indicesMetadata,
  groupedTimesAndWords,
  displayText,
}: {
  groupedTimesAndWords: MentionCard["grouped_times_and_words"];
  trimmedDisplayText: MentionV4["trimmed_display_text"];
  indicesMetadata?: MentionV4["indices_metadata"];
  displayText: MentionV4["display_text"];
}) {
  const mediaCurrentTime =
    useMediaSelector((state) => state.mediaCurrentTime) ?? 0;
  const mediaPaused = useMediaSelector((state) => state.mediaPaused);

  const isWithinMention = useMemo(() => {
    if (!indicesMetadata) return false;
    return (
      mediaCurrentTime >= indicesMetadata.start_time &&
      mediaCurrentTime <= indicesMetadata.end_time
    );
  }, [mediaCurrentTime, indicesMetadata]);

  const isPlaying = isWithinMention && !mediaPaused;

  const wordsWithTimeCodes = useMemo((): Array<{
    word: string;
    timeCode?: number;
    isMatch?: boolean;
  }> => {
    const words = displayText.split(" ").filter(Boolean);

    const fallback = words.map((word) => ({ word }));

    if (!groupedTimesAndWords || !indicesMetadata) {
      return fallback;
    }

    const firstIndex = groupedTimesAndWords.times.findIndex(
      (time) => Math.round(time) === Math.round(indicesMetadata.start_time)
    );

    if (firstIndex === -1) {
      return fallback;
    }

    return words.map((rawWord, i) => {
      const hasOpeningBoldTag = rawWord.includes("<b>");
      const hasClosingBoldTag = rawWord.includes("</b>");
      const isInsideMatch = hasOpeningBoldTag
        ? true
        : hasClosingBoldTag
          ? false
          : false;
      const isMatch = isInsideMatch || hasOpeningBoldTag;

      return {
        timeCode: groupedTimesAndWords.times[firstIndex + i],
        word: isMatch
          ? rawWord.replaceAll("<b>", "").replaceAll("</b>", "")
          : rawWord,
        isMatch,
      };
    });
  }, [displayText, indicesMetadata, groupedTimesAndWords]);

  return (
    <p className="mention text-sm leading-6.5 text-balance">
      {wordsWithTimeCodes.map(({ word, isMatch, timeCode }, i) => {
        const isProbablySpokenRightNow =
          typeof timeCode === "number" ? timeCode < mediaCurrentTime : false;

        return (
          <Fragment key={`${word}-${i}`}>
            <span
              className={cn(
                "rounded-xs transition",
                isMatch && "py-px px-0.5 bg-violet-200 font-bold",
                !isMatch &&
                  isProbablySpokenRightNow &&
                  isPlaying &&
                  "bg-violet-200"
              )}
            >
              {word}
            </span>
            {i < wordsWithTimeCodes.length - 1 ? (
              <span
                className={
                  isProbablySpokenRightNow && isPlaying
                    ? "transition bg-violet-200"
                    : "transition"
                }
              >
                {" "}
              </span>
            ) : null}
          </Fragment>
        );
      })}
    </p>
  );
}
